import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardBody } from "../../design-system/Card";
import { Button } from "../../design-system/Button";
import { Badge } from "../../design-system/Badge";
import { Icon } from "../../design-system/Icon";
import { useIpc } from "../../ipc/IpcProvider";
import type { MlCategory, TrendItem, TrendAnalysis, SignalTone, TrendHistory, TrendHistoryItem, VelocityTrend, TrendClassKind, ReviewIntelResult, OpenInvestigationInput, WatchedCategory, WatchRunResult, WatchFindingKind } from "../../ipc/types";
import "../product-development/product-development.css";

const clp = (minor: number): string => `$${Math.round(minor).toLocaleString("es-CL")}`;
const SIGNAL_TONE: Record<SignalTone, "success" | "warning" | "neutral"> = { opportunity: "success", warning: "warning", info: "neutral" };
const CLASS_TONE: Record<TrendClassKind, "success" | "brand" | "neutral" | "danger"> = { emergente: "brand", "en-auge": "success", maduro: "neutral", "en-declive": "danger" };
const WATCH_META: Record<WatchFindingKind, { label: string; tone: "success" | "brand" | "warning" }> = {
  "en-auge": { label: "En auge", tone: "success" }, "weak-spot": { label: "Oportunidad", tone: "brand" }, "market-gap": { label: "Hueco", tone: "warning" },
};
const TREND_META: Record<VelocityTrend, { label: string; tone: "success" | "danger" | "neutral"; arrow: string }> = {
  subiendo: { label: "Subiendo", tone: "success", arrow: "↗" },
  bajando: { label: "Bajando", tone: "danger", arrow: "↘" },
  estable: { label: "Estable", tone: "neutral", arrow: "→" },
  "sin-datos": { label: "Sin datos", tone: "neutral", arrow: "·" },
};

/** Mini-gráfico de la POSICIÓN en el tiempo (arriba = mejor posición, invertido). */
function Sparkline({ item }: { item: TrendHistoryItem }): JSX.Element {
  const pts = item.points;
  const w = 120, h = 32, pad = 3;
  if (pts.length < 2) return <span className="faint" style={{ fontSize: 12 }}>1 día</span>;
  const positions = pts.map((p) => p.position);
  const min = Math.min(...positions), max = Math.max(...positions);
  const span = max - min || 1;
  const coords = pts.map((p, i) => {
    const x = pad + (i * (w - 2 * pad)) / (pts.length - 1);
    // Posición baja (mejor) → arriba (y pequeño). Invertimos.
    const y = pad + ((p.position - min) / span) * (h - 2 * pad);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const color = item.velocity.trend === "subiendo" ? "var(--success)" : item.velocity.trend === "bajando" ? "var(--danger)" : "var(--text-faint)";
  const lastX = pad + (w - 2 * pad), lastY = coords[coords.length - 1]!.split(",")[1];
  return (
    <svg width={w} height={h} style={{ display: "block" }} aria-label="Trayectoria de posición">
      <polyline points={coords.join(" ")} fill="none" stroke={color} strokeWidth={1.8} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lastX} cy={Number(lastY)} r={2.6} fill={color} />
    </svg>
  );
}

/** Etiqueta de cambio de posición (momentum) respecto a la captura anterior. */
function ChangeCell({ it }: { it: TrendItem }): JSX.Element {
  if (it.isNew) return <Badge tone="brand" dot>Nuevo</Badge>;
  if (it.deltaPosition === undefined) return <span className="faint">—</span>;
  if (it.deltaPosition > 0) return <span style={{ color: "var(--success)" }} className="strong">↑ {it.deltaPosition}</span>;
  if (it.deltaPosition < 0) return <span style={{ color: "var(--danger)" }} className="strong">↓ {Math.abs(it.deltaPosition)}</span>;
  return <span className="muted">=</span>;
}

/** Puntaje DSM 0–100 con barra y color según el valor. */
function ScoreChip({ value }: { value: number }): JSX.Element {
  const color = value >= 65 ? "var(--success)" : value >= 40 ? "var(--warning)" : "var(--text-faint)";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 92 }}>
      <div style={{ flex: 1, height: 6, borderRadius: 4, background: "color-mix(in srgb, var(--text-faint) 22%, transparent)", overflow: "hidden" }}>
        <div style={{ width: `${value}%`, height: "100%", background: color }} />
      </div>
      <span className="strong" style={{ fontSize: 12.5, minWidth: 22, textAlign: "right", color }}>{value}</span>
    </div>
  );
}

/** Señales "día 1" (oportunidad/aviso) del producto, más la pista de oportunidad si es un más-vendido con debilidad. */
function SignalCell({ it }: { it: TrendItem }): JSX.Element {
  if (it.signals.length === 0) return <span className="faint">—</span>;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {it.signals.map((s, i) => <Badge key={i} tone={SIGNAL_TONE[s.tone]} dot={s.tone === "opportunity"}>{s.label}</Badge>)}
      {it.opportunityHint ? <div className="faint" style={{ fontSize: 11.5, flexBasis: "100%", marginTop: 2 }}>💡 {it.opportunityHint}</div> : null}
    </div>
  );
}

/**
 * IA → Tendencias Mercado Libre Chile (Fase 2). Selector jerárquico de categorías + "más vendidos"
 * REALES (Highlights) con precio y sold_quantity observados. La velocidad/tendencia (Δ en el tiempo)
 * y los scores llegan en fases siguientes al acumular snapshots.
 */
export function MarketTrendsScreen(): JSX.Element {
  const client = useIpc();
  const navigate = useNavigate();
  // Drill-down por niveles: `levels[i]` = opciones del nivel i; `chosen[i]` = id elegido en ese nivel.
  const [levels, setLevels] = useState<readonly (readonly MlCategory[])[]>([]);
  const [chosen, setChosen] = useState<readonly string[]>([]);
  const [isLeaf, setIsLeaf] = useState(false);
  const [result, setResult] = useState<TrendAnalysis | null>(null);
  const [busy, setBusy] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [history, setHistory] = useState<TrendHistory | null>(null);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [review, setReview] = useState<ReviewIntelResult | null>(null);
  const [reviewBusy, setReviewBusy] = useState<string | null>(null); // itemId en carga
  const [strategy, setStrategy] = useState<string | null>(null);
  const [strategyBusy, setStrategyBusy] = useState(false);
  const [watchItems, setWatchItems] = useState<readonly WatchedCategory[]>([]);
  const [watchBusy, setWatchBusy] = useState(false);
  const [watchRun, setWatchRun] = useState<WatchRunResult | null>(null);
  const [onlyWeak, setOnlyWeak] = useState(false);
  const [sortBy, setSortBy] = useState<"position" | "opportunity" | "demand">("position");
  const [msg, setMsg] = useState<string | null>(null);
  const allItems = result?.items ?? null;
  const items = (() => {
    if (!allItems) return null;
    const filtered = onlyWeak ? allItems.filter((it) => it.weakSpot) : allItems;
    const cmp = sortBy === "opportunity" ? (a: TrendItem, b: TrendItem) => b.entryOpportunity - a.entryOpportunity
      : sortBy === "demand" ? (a: TrendItem, b: TrendItem) => b.demandMomentum - a.demandMomentum
      : (a: TrendItem, b: TrendItem) => a.position - b.position;
    return [...filtered].sort(cmp);
  })();

  // Carga inicial de categorías de primer nivel.
  useEffect(() => {
    let active = true;
    client.getTrendCategories().then((r) => {
      if (!active) return;
      if (r.ok && r.value.ok) setLevels([r.value.categories]);
      else setMsg(r.ok ? r.value.detail : r.error.message);
    });
    client.getWatchlist().then((r) => { if (active && r.ok) setWatchItems(r.value.items); });
    return () => { active = false; };
  }, [client]);

  const targetId = chosen.length > 0 ? chosen[chosen.length - 1] ?? "" : "";
  const targetName = (() => { for (const lvl of levels) { const f = lvl.find((c) => c.id === targetId); if (f) return f.name; } return ""; })();

  /** Construye el candidato de Desarrollo de Producto a partir de la inteligencia del más-vendido. */
  const seedFrom = (it: TrendItem, name?: string): OpenInvestigationInput => ({
    name: name ?? it.title,
    category: targetName || "Mercado Libre",
    demand: it.demandMomentum,
    competition: it.sellers > 0 ? Math.min(100, it.sellers * 4) : Math.max(0, 100 - it.entryOpportunity),
    marginPct: 40,
    aiConfidence: it.entryOpportunity,
    leadTimeDays: 30,
  });
  const toProductDevelopment = (it: TrendItem, name?: string): void => {
    navigate("/desarrollo-producto", { state: { pdSeed: seedFrom(it, name) } });
  };

  const watched = watchItems.some((w) => w.categoryId === targetId);
  const toggleWatch = async (): Promise<void> => {
    if (!targetId) return;
    const r = watched ? await client.removeFromWatchlist(targetId) : await client.addToWatchlist(targetId, targetName || targetId);
    if (r.ok) setWatchItems(r.value.items);
  };
  const removeWatch = async (categoryId: string): Promise<void> => {
    const r = await client.removeFromWatchlist(categoryId);
    if (r.ok) setWatchItems(r.value.items);
  };
  const runWatchNow = async (): Promise<void> => {
    setWatchBusy(true); setWatchRun(null); setMsg(null);
    const r = await client.runWatch();
    setWatchBusy(false);
    if (!r.ok) { setMsg(r.error.message); return; }
    setWatchRun(r.value); setMsg(r.value.detail);
  };

  /** Estrategia IA: sintetiza tendencia + scores + huecos de opiniones en un plan para GANARLE al líder. */
  const generateStrategy = async (): Promise<void> => {
    if (!review) return;
    const it = result?.items.find((i) => i.itemId === review.itemId);
    const rep = review.report;
    setStrategyBusy(true); setStrategy(null); setMsg(null);
    const prompt = [
      "Eres un asesor de e-commerce para un vendedor de Mercado Libre Chile (precios en CLP, IVA 19%).",
      "Con estos datos de inteligencia de mercado de un producto LÍDER, propón una estrategia concreta para lanzar un producto competidor que le gane.",
      `Producto líder: "${review.title}"${targetName ? ` (categoría ${targetName})` : ""}.`,
      it ? `Fase de ciclo de vida: ${it.classification.label}. Demanda ${it.demandMomentum}/100. Oportunidad de entrada ${it.entryOpportunity}/100. Competencia: ${it.sellers || "n/d"} vendedores. Precio de referencia: $${it.priceMinor.toLocaleString("es-CL")}.` : "",
      `Voz del comprador: promedio ${rep.avgRating}★, ${rep.negativeShare}% de opiniones en 1–2★ sobre ${rep.reviewsTotal} opiniones.`,
      rep.gaps.length > 0 ? `Huecos detectados: ${rep.gaps.map((g) => `${g.label} → ${g.suggestion}`).join("; ")}.` : "No se detectaron quejas textuales; la ventaja tendrá que venir de precio, envío o servicio.",
      "Responde en español, conciso y accionable, con estas secciones numeradas: 1) ¿Conviene entrar? (sí/no y por qué). 2) 3 diferenciadores clave basados en los huecos. 3) Un título de publicación sugerido (máx. 60 caracteres). 4) Precio de entrada sugerido en CLP. 5) 2 riesgos a cuidar.",
    ].filter(Boolean).join("\n");
    const r = await client.askAi(prompt);
    setStrategyBusy(false);
    if (!r.ok) { setMsg(r.error.message); return; }
    setStrategy(r.value.ok ? r.value.answer : (r.value.answer || "La IA no devolvió texto. Revisa la configuración de IA en Seguridad."));
  };

  // Al elegir en el nivel i: fija ese id, poda niveles más profundos y carga los hijos del elegido.
  const onSelect = async (levelIdx: number, id: string): Promise<void> => {
    setResult(null); setHistory(null); setReview(null); setStrategy(null); setMsg(null);
    const nextChosen = chosen.slice(0, levelIdx);
    if (id) nextChosen[levelIdx] = id;
    setChosen(nextChosen);
    setLevels((prev) => prev.slice(0, levelIdx + 1));
    setIsLeaf(false);
    if (!id) return;
    const r = await client.getTrendSubcategories(id);
    if (r.ok && r.value.ok && r.value.categories.length > 0) {
      setLevels((prev) => prev.slice(0, levelIdx + 1).concat([r.value.categories]));
    } else {
      setIsLeaf(true); // categoría específica (hoja): aquí sí hay ranking de más vendidos
    }
  };

  const analyze = async (): Promise<void> => {
    if (!targetId) { setMsg("Elige una categoría."); return; }
    setBusy(true); setMsg(null); setResult(null); setReview(null); setStrategy(null); setOnlyWeak(false); setSortBy("position");
    const r = await client.analyzeTrends(targetId);
    setBusy(false);
    if (!r.ok) { setMsg(r.error.message); return; }
    setResult(r.value);
    setMsg(r.value.detail);
  };
  const capture = async (): Promise<void> => {
    if (!targetId) return;
    setCapturing(true); setMsg(null);
    const r = await client.captureTrendSnapshot(targetId);
    setCapturing(false);
    setMsg(r.ok ? r.value.detail : r.error.message);
    if (r.ok && r.value.ok) { await analyze(); if (history) await loadHistory(); } // refresca análisis e histórico
  };
  const loadReviews = async (it: TrendItem): Promise<void> => {
    setReviewBusy(it.itemId); setReview(null); setStrategy(null);
    const r = await client.getReviewIntel(it.itemId, it.title);
    setReviewBusy(null);
    if (!r.ok) { setMsg(r.error.message); return; }
    setReview(r.value);
    if (!r.value.ok || r.value.report.reviewsTotal === 0) setMsg(r.value.detail);
  };
  const loadHistory = async (): Promise<void> => {
    if (!targetId) { setMsg("Elige una categoría."); return; }
    setHistoryBusy(true); setMsg(null);
    const r = await client.getTrendHistory(targetId);
    setHistoryBusy(false);
    if (!r.ok) { setMsg(r.error.message); return; }
    setHistory(r.value);
    if (r.value.items.length === 0) setMsg("Aún no hay histórico: captura un snapshot hoy y otro mañana para ver la trayectoria.");
  };

  return (
    <div>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="row" style={{ gap: "var(--sp-4)" }}>
            <div className="sidebar__logo" style={{ width: 44, height: 44 }}><Icon name="spark" size={22} /></div>
            <div>
              <h1 className="pagehead__title">Tendencias Mercado Libre Chile</h1>
              <p className="pagehead__sub">Más vendidos reales por categoría (datos observados de Mercado Libre). La velocidad y los scores llegan al acumular histórico.</p>
            </div>
          </div>
        </div>
      </div>

      <Card style={{ marginBottom: "var(--sp-5)" }}>
        <CardHeader
          title={<span className="row" style={{ gap: 8 }}><Icon name="bell" size={18} /> Vigilancia automática</span>}
          subtitle="Las categorías que sigues se capturan y analizan solas (al abrir la app y al sincronizar); los hallazgos nuevos llegan al centro de avisos y al Dashboard."
          action={<Button size="sm" icon="pulse" disabled={watchBusy || watchItems.length === 0} onClick={runWatchNow}>{watchBusy ? "Revisando…" : "Ejecutar vigilancia ahora"}</Button>}
        />
        <CardBody>
          {watchItems.length === 0 ? (
            <div className="faint" style={{ fontSize: 13 }}>Aún no sigues ninguna categoría. Elige una abajo y pulsa <b className="strong">“Vigilar”</b> para que la app la revise sola cada día.</div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {watchItems.map((w) => (
                <span key={w.categoryId} className="badge badge--neutral" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {w.name}
                  <button type="button" aria-label={`Dejar de vigilar ${w.name}`} style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", padding: 0, fontSize: 13, lineHeight: 1 }} onClick={() => removeWatch(w.categoryId)}>✕</button>
                </span>
              ))}
            </div>
          )}
          {watchRun ? (
            watchRun.newFindings.length > 0 ? (
              <div style={{ marginTop: "var(--sp-4)" }}>
                <div className="strong" style={{ marginBottom: "var(--sp-2)" }}>Novedades de esta revisión</div>
                <div style={{ display: "grid", gap: 6 }}>
                  {watchRun.newFindings.map((f, i) => (
                    <div key={i} className="row" style={{ gap: 10, fontSize: 13, alignItems: "baseline" }}>
                      <Badge tone={WATCH_META[f.kind].tone} dot>{WATCH_META[f.kind].label}</Badge>
                      <span className="strong">{f.title}</span>
                      <span className="faint">{f.detail}{f.categoryName ? ` · ${f.categoryName}` : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="faint" style={{ fontSize: 12.5, marginTop: "var(--sp-3)" }}>✓ {watchRun.detail}</div>
            )
          ) : null}
        </CardBody>
      </Card>

      <Card style={{ marginBottom: "var(--sp-5)" }}>
        <CardHeader title="Elige qué analizar" subtitle="Baja por el árbol hasta una categoría específica: el ranking de más vendidos existe en las categorías finales, no en los departamentos generales." />
        <CardBody>
          <div className="pd-form">
            {levels.map((options, i) => (
              <div className="pd-field" key={i}>
                <span className="pd-field__label">{i === 0 ? "Categoría" : `Nivel ${i + 1}`}</span>
                <select className="pd-input" aria-label={i === 0 ? "Categoría" : `Nivel ${i + 1}`} value={chosen[i] ?? ""} onChange={(e) => onSelect(i, e.target.value)}>
                  <option value="">{i === 0 ? "— Elige una categoría —" : "— Elige una subcategoría —"}</option>
                  {options.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            ))}
          </div>
          {targetId ? (
            <div className="muted" style={{ fontSize: 12.5, marginTop: "var(--sp-2)" }}>
              {isLeaf ? "✓ Categoría específica: aquí hay ranking de más vendidos." : "Esta categoría aún tiene subcategorías — baja un nivel más para ver el ranking real."}
            </div>
          ) : null}
          <div className="pd-actions">
            {msg ? <span className="faint" style={{ fontSize: 12.5, marginRight: "auto" }}>{msg}</span> : null}
            {targetId ? <Button size="sm" variant={watched ? "secondary" : "ghost"} icon="bell" onClick={toggleWatch}>{watched ? "✓ Vigilando" : "Vigilar"}</Button> : null}
            <Button size="sm" variant="ghost" icon="analytics" disabled={historyBusy || busy || !targetId} onClick={loadHistory}>{historyBusy ? "Cargando…" : "Ver histórico"}</Button>
            <Button size="sm" variant="secondary" icon="pulse" disabled={capturing || busy || !targetId} onClick={capture}>{capturing ? "Guardando…" : "Capturar snapshot"}</Button>
            <Button size="sm" icon="spark" disabled={busy || !targetId} onClick={analyze}>{busy ? "Analizando…" : "Analizar tendencias"}</Button>
          </div>
        </CardBody>
      </Card>

      {allItems ? (
        <Card>
          <CardHeader
            title="Más vendidos"
            subtitle={`${allItems.length} productos · dato OBSERVADO · "Vendedores" = competencia real${result && result.capturedDays > 1 && result.comparedTo ? ` · Cambio vs. ${result.comparedTo} (${result.capturedDays} días de histórico)` : " · captura un snapshot cada día para ver el momentum"}`}
            action={
              <div className="row" style={{ gap: "var(--sp-2)" }}>
                <select className="pd-input" aria-label="Ordenar por" style={{ height: 30, fontSize: 12.5, padding: "0 8px" }} value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)}>
                  <option value="position">Orden: Posición</option>
                  <option value="opportunity">Orden: Oportunidad</option>
                  <option value="demand">Orden: Demanda</option>
                </select>
                {result && result.weakSpots > 0 ? (
                  <Button size="sm" variant={onlyWeak ? "primary" : "secondary"} icon="pulse" onClick={() => setOnlyWeak((v) => !v)}>
                    {onlyWeak ? `Viendo ${result.weakSpots} con debilidad` : `${result.weakSpots} con debilidad`}
                  </Button>
                ) : null}
                <Badge tone="info">Highlights</Badge>
              </div>
            }
          />
          <div className="card__body" style={{ paddingTop: 0, display: "flex", flexDirection: "column", gap: 6 }}>
            {result && result.weakSpots > 0 && !onlyWeak ? (
              <div className="faint" style={{ fontSize: 12.5 }}>💡 <b className="strong">{result.weakSpots}</b> más-vendido(s) con debilidad explotable: alta demanda + poca competencia o precio alto. Filtra para verlos solos.</div>
            ) : null}
            <div className="faint" style={{ fontSize: 12, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
              <span>Fase (ciclo de vida):</span>
              <Badge tone="brand" dot>Emergente</Badge><span>entra con tracción</span>
              <Badge tone="success" dot>En auge</Badge><span>alta demanda y subiendo</span>
              <Badge tone="neutral" dot>Maduro</Badge><span>demanda estable</span>
              <Badge tone="danger" dot>En declive</Badge><span>pierde posiciones</span>
            </div>
          </div>
          <div className="card__body" style={{ padding: 0 }}>
            {allItems.length === 0 ? (
              <div className="statecard"><Icon name="spark" size={26} style={{ color: "var(--text-faint)" }} /><div className="strong">Sin ranking en esta categoría</div><div className="faint" style={{ fontSize: 13 }}>Suele ser una categoría demasiado general. <b className="strong">Baja un nivel más</b> (elige una subcategoría) — el ranking de más vendidos aparece en las categorías específicas.</div></div>
            ) : items && items.length === 0 ? (
              <div className="statecard"><Icon name="pulse" size={26} style={{ color: "var(--text-faint)" }} /><div className="strong">Ninguno con debilidad clara</div><div className="faint" style={{ fontSize: 13 }}>En esta categoría los más vendidos no muestran una ventana obvia (competencia alta y precios parejos). Quita el filtro para ver el ranking completo.</div></div>
            ) : (
              <table className="table">
                <thead><tr><th>#</th><th>Cambio</th><th>Producto</th><th>Fase</th><th title="Demand Momentum: cuánta demanda tiene y si sube en el ranking">Demanda</th><th title="Entry Opportunity: qué tan buena ventana hay para entrar (poca competencia + margen de precio, ponderado por demanda)">Oportunidad</th><th>Precio</th><th>Vendedores</th><th>Señales</th></tr></thead>
                <tbody>
                  {(items ?? []).map((it) => (
                    <tr key={it.itemId} style={it.weakSpot ? { background: "color-mix(in srgb, var(--success) 7%, transparent)" } : undefined}>
                      <td className="strong">{it.position}</td>
                      <td><ChangeCell it={it} /></td>
                      <td>
                        <div className="strong">{it.weakSpot ? "★ " : ""}{it.title}</div>
                        <div className="faint" style={{ fontSize: 12, display: "flex", gap: 8, alignItems: "center" }}>
                          <span>{it.itemId}</span>
                          <button type="button" style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--brand)", fontSize: 12 }} disabled={reviewBusy !== null} onClick={() => loadReviews(it)}>
                            {reviewBusy === it.itemId ? "Analizando…" : "🔍 Opiniones"}
                          </button>
                          <button type="button" title="Llevar a Desarrollo de Producto con las señales precargadas" style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--brand)", fontSize: 12 }} onClick={() => toProductDevelopment(it)}>
                            🚀 Investigar
                          </button>
                        </div>
                      </td>
                      <td><span title={it.classification.note}><Badge tone={CLASS_TONE[it.classification.kind]} dot>{it.classification.label}</Badge></span></td>
                      <td><ScoreChip value={it.demandMomentum} /></td>
                      <td><ScoreChip value={it.entryOpportunity} /></td>
                      <td className="strong">{clp(it.priceMinor)}</td>
                      <td className="strong">{it.sellers > 0 ? it.sellers.toLocaleString("es-CL") : "—"}</td>
                      <td><SignalCell it={it} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      ) : null}

      {review && review.report.reviewsTotal > 0 ? (
        <Card style={{ marginTop: "var(--sp-5)" }}>
          <CardHeader
            title="Opiniones y huecos de mercado"
            subtitle={`${review.title} · ${review.report.reviewsTotal} opiniones · ${review.report.avgRating}★ · ${review.report.negativeShare}% en 1–2★ · ${review.report.withText} con texto`}
            action={
              <div className="row" style={{ gap: "var(--sp-2)" }}>
                <Button size="sm" icon="ai" disabled={strategyBusy} onClick={generateStrategy}>{strategyBusy ? "Pensando…" : "Estrategia IA"}</Button>
                <Badge tone={review.report.gaps.length > 0 ? "warning" : "success"}>{review.report.gaps.length > 0 ? "Hay huecos" : "Sin quejas claras"}</Badge>
              </div>
            }
          />
          <CardBody>
            {review.report.distribution ? (() => {
              const d = review.report.distribution;
              const tot = d.one + d.two + d.three + d.four + d.five || 1;
              const rows: { label: string; n: number; tone: string }[] = [
                { label: "5★", n: d.five, tone: "var(--success)" }, { label: "4★", n: d.four, tone: "var(--success)" },
                { label: "3★", n: d.three, tone: "var(--warning)" }, { label: "2★", n: d.two, tone: "var(--danger)" }, { label: "1★", n: d.one, tone: "var(--danger)" },
              ];
              return (
                <div style={{ marginBottom: "var(--sp-4)", display: "grid", gap: 4, maxWidth: 420 }}>
                  <div className="strong" style={{ fontSize: 12.5, marginBottom: 2 }}>Distribución de estrellas (dato real de ML)</div>
                  {rows.map((r) => (
                    <div key={r.label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                      <span style={{ width: 24 }}>{r.label}</span>
                      <div style={{ flex: 1, height: 8, borderRadius: 4, background: "color-mix(in srgb, var(--text-faint) 22%, transparent)", overflow: "hidden" }}>
                        <div style={{ width: `${(r.n / tot) * 100}%`, height: "100%", background: r.tone }} />
                      </div>
                      <span className="faint" style={{ width: 28, textAlign: "right" }}>{r.n}</span>
                    </div>
                  ))}
                </div>
              );
            })() : null}
            <div className="statecard" style={{ alignItems: "flex-start", textAlign: "left", background: "color-mix(in srgb, var(--brand) 6%, transparent)", marginBottom: "var(--sp-4)" }}>
              <div className="strong">🎯 Veredicto</div>
              <div style={{ fontSize: 13 }}>{review.report.verdict}</div>
            </div>
            {review.report.gaps.length > 0 ? (
              <>
                <div className="strong" style={{ marginBottom: "var(--sp-2)" }}>Huecos priorizados (qué mejorar para ganarles)</div>
                <table className="table">
                  <thead><tr><th>Tema</th><th>Señales</th><th>Cómo lo aprovechas</th></tr></thead>
                  <tbody>
                    {review.report.gaps.map((g) => (
                      <tr key={g.id}>
                        <td className="strong">{g.label}</td>
                        <td><Badge tone="warning">{g.count}</Badge></td>
                        <td style={{ fontSize: 13 }}>{g.suggestion}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {review.report.complaints.some((c) => c.sample) ? (
                  <div style={{ marginTop: "var(--sp-4)" }}>
                    <div className="strong" style={{ marginBottom: "var(--sp-2)" }}>Quejas textuales (ejemplos)</div>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                      {review.report.complaints.filter((c) => c.sample).map((c) => (
                        <li key={c.id} style={{ marginBottom: 4 }}><b className="strong">{c.label}</b> (×{c.count}): <span className="faint">“{c.sample}”</span></li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </>
            ) : (
              <div className="faint" style={{ fontSize: 13 }}>Las opiniones no muestran un patrón de queja claro: el líder cumple bien. Tu ventaja tendrá que venir de precio, envío o servicio.</div>
            )}
          </CardBody>
        </Card>
      ) : null}

      {strategy ? (
        <Card style={{ marginTop: "var(--sp-5)" }}>
          <CardHeader
            title="Estrategia con IA"
            subtitle={review ? `Plan para ganarle a “${review.title}”` : "Plan de entrada"}
            action={
              <Button size="sm" variant="secondary" icon="flask" onClick={() => {
                const it = result?.items.find((i) => i.itemId === review?.itemId);
                if (it) toProductDevelopment(it, `${it.title} (versión mejorada)`);
              }}>Llevar a Desarrollo de Producto</Button>
            }
          />
          <CardBody>
            <div style={{ whiteSpace: "pre-wrap", fontSize: 13.5, lineHeight: 1.55 }}>{strategy}</div>
            <div className="faint" style={{ fontSize: 11.5, marginTop: "var(--sp-3)" }}>Generado por IA a partir de datos observados de Mercado Libre. Revisa antes de publicar.</div>
          </CardBody>
        </Card>
      ) : null}

      {history && history.items.length > 0 ? (
        <Card style={{ marginTop: "var(--sp-5)" }}>
          <CardHeader
            title="Trayectoria (histórico)"
            subtitle={`${history.days.length} días capturados (${history.days[0]} → ${history.days[history.days.length - 1]}) · tendencia sostenida de posición y precio`}
            action={<Badge tone="brand">Velocidad</Badge>}
          />
          <div className="card__body" style={{ padding: 0 }}>
            <table className="table">
              <thead><tr><th>Producto</th><th>Trayectoria (posición)</th><th>Tendencia</th><th>Δ posición total</th><th>Precio</th></tr></thead>
              <tbody>
                {history.items.map((it) => {
                  const meta = TREND_META[it.velocity.trend];
                  return (
                    <tr key={it.itemId}>
                      <td><div className="strong">{it.title}</div><div className="faint" style={{ fontSize: 12 }}>ahora #{it.lastPosition} · {it.velocity.daysTracked} días</div></td>
                      <td><Sparkline item={it} /></td>
                      <td><Badge tone={meta.tone} dot>{meta.arrow} {meta.label}</Badge></td>
                      <td className="strong" style={{ color: it.velocity.netPositionChange > 0 ? "var(--success)" : it.velocity.netPositionChange < 0 ? "var(--danger)" : undefined }}>
                        {it.velocity.netPositionChange > 0 ? `↑ ${it.velocity.netPositionChange}` : it.velocity.netPositionChange < 0 ? `↓ ${Math.abs(it.velocity.netPositionChange)}` : "="}
                      </td>
                      <td><div className="strong">{clp(it.lastPriceMinor)}</div>{it.velocity.priceChangePct !== 0 ? <div className="faint" style={{ fontSize: 12, color: it.velocity.priceChangePct < 0 ? "var(--success)" : "var(--danger)" }}>{it.velocity.priceChangePct > 0 ? "+" : ""}{it.velocity.priceChangePct}%</div> : null}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
